import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card,
  Button,
  Input,
  InputNumber,
  Select,
  Table,
  Tag,
  Space,
  message,
  Divider,
  Typography,
  Form,
  Collapse,
  Empty,
  DatePicker,
  Tabs,
  Drawer,
  Modal,
} from 'antd';
import {
  DollarOutlined,
  ReloadOutlined,
  CheckOutlined,
  EnvironmentOutlined,
  TableOutlined,
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { apiClient } from '../../../config/apiClient';
import MainLayout from '../../layout/layout';
import { usePermissions } from '../../../context/permissionsContext';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { Panel } = Collapse;

const Cobros = () => {
  const { hasPermission } = usePermissions();
  const [destinos, setDestinos] = useState([]);
  const [ventasPorDestino, setVentasPorDestino] = useState({});
  const [formasPago, setFormasPago] = useState([]);
  const [jugadores, setJugadores] = useState([]);
  const [jugadoresSeleccionados, setJugadoresSeleccionados] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingPago, setLoadingPago] = useState({});
  const [montosPago, setMontosPago] = useState({});
  const [formasPagoSeleccionadas, setFormasPagoSeleccionadas] = useState({});
  const [descuentosPorcentaje, setDescuentosPorcentaje] = useState({});
  const [expandedDestinos, setExpandedDestinos] = useState({});
  const [expandedPanelsDestino, setExpandedPanelsDestino] = useState({});
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 768);
  // Estado para pago unificado (varias ventas)
  const [montoPagoUnificado, setMontoPagoUnificado] = useState({});
  const [formaPagoUnificada, setFormaPagoUnificada] = useState({});
  const [descuentoUnificado, setDescuentoUnificado] = useState({});
  const [jugadorUnificado, setJugadorUnificado] = useState({});
  const [loadingPagoUnificado, setLoadingPagoUnificado] = useState({});
  const [turnos, setTurnos] = useState([]);
  const [fechaCobros, setFechaCobros] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [selectedItem, setSelectedItem] = useState(null);
  const [vistaCobros, setVistaCobros] = useState('canchas'); // 'canchas' | 'mesas'
  const [mesasParaCobros, setMesasParaCobros] = useState([]);
  const [loadingMesas, setLoadingMesas] = useState(false);
  // Agregar venta desde Cobros
  const [modalAgregarVentaVisible, setModalAgregarVentaVisible] = useState(false);
  const [destinoParaVenta, setDestinoParaVenta] = useState(null);
  const [articulos, setArticulos] = useState([]);
  const [detallesTemporal, setDetallesTemporal] = useState([]);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [cantidadTemporal, setCantidadTemporal] = useState(1);
  const [loadingAgregarVenta, setLoadingAgregarVenta] = useState(false);
  const [mesasVinculadasSeleccionadas, setMesasVinculadasSeleccionadas] = useState([]);
  const [eliminandoDetalleId, setEliminandoDetalleId] = useState(null);
  const [responsableMesa, setResponsableMesa] = useState({});
  const articuloSelectRef = useRef(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const loadDestinos = async (fechaOverride) => {
    const fecha = fechaOverride ?? fechaCobros;
    setLoading(true);
    try {
      const response = await apiClient.get(`/gestion/cobros/destinos-pendientes?fecha=${fecha}`);
      if (response.ok) {
        const data = await response.json();
        setDestinos(data || []);
        // Pre-cargar ventas de cada destino para tener el turno de caja correcto desde el inicio
        // (mesas y canchas obtienen el turno de las ventas; sin esto aparecen "Sin turno" hasta expandir)
        const destinosList = data || [];
        destinosList.forEach((d) => loadVentasPorDestino(d.tipo, d.id, { fecha }));
      } else {
        message.error('Error al cargar destinos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cargar destinos');
    } finally {
      setLoading(false);
    }
  };

  const loadFormasPago = async () => {
    try {
      const response = await apiClient.get('/gestion/cobros/recursos/formas-pago');
      if (response.ok) {
        const data = await response.json();
        setFormasPago(data || []);
      }
    } catch (error) {
      console.error('Error al cargar formas de pago:', error);
    }
  };

  const loadJugadores = async () => {
    try {
      const response = await apiClient.get('/club/jugadores');
      if (response.ok) {
        const data = await response.json();
        setJugadores(data || []);
      }
    } catch (error) {
      console.error('Error al cargar jugadores:', error);
    }
  };

  const loadTurnos = async () => {
    try {
      const response = await apiClient.get('/gestion/cobros/recursos/turnos?activos=false');
      if (response.ok) {
        const data = await response.json();
        setTurnos(data || []);
      }
    } catch (error) {
      console.error('Error al cargar turnos:', error);
    }
  };

  const loadMesasParaCobros = async (fechaOverride) => {
    const fecha = fechaOverride ?? fechaCobros;
    setLoadingMesas(true);
    try {
      const response = await apiClient.get(`/gestion/cobros/mesas?fecha=${fecha}`);
      if (response.ok) {
        const data = await response.json();
        setMesasParaCobros(data || []);
        (data || []).forEach((m) => loadVentasPorDestino('mesa', m.id, { fecha }));
      } else {
        message.error('Error al cargar mesas');
      }
    } catch (error) {
      console.error('Error al cargar mesas:', error);
      message.error('Error al cargar mesas');
    } finally {
      setLoadingMesas(false);
    }
  };

  const loadArticulos = async () => {
    try {
      const response = await apiClient.get('/gestion/ventas/recursos/articulos');
      if (response.ok) {
        const data = await response.json();
        setArticulos(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error al cargar artículos:', error);
    }
  };

  const loadVentasPorDestino = async (tipo, destinoId, opts = {}) => {
    const { fecha: fechaOpt } = opts;
    const key = `${tipo}-${destinoId}`;
    const params = new URLSearchParams({ tipo, destino_id: destinoId });
    const fechaUsar = fechaOpt ?? fechaCobros;
    if ((tipo === 'mesa' || tipo === 'cancha') && fechaUsar) {
      params.set('fecha', fechaUsar);
    }
    try {
      const response = await apiClient.get(
        `/gestion/cobros/ventas-pendientes?${params.toString()}`
      );
      if (response.ok) {
        const data = await response.json();
        setVentasPorDestino(prev => ({
          ...prev,
          [key]: data || []
        }));
        return data || [];
      }
    } catch (error) {
      console.error('Error al cargar ventas:', error);
      message.error('Error al cargar las ventas');
    }
    return [];
  };

  const getTurnoCajaPorHora = (horaStr) => {
    if (!horaStr || !turnos || turnos.length === 0) return undefined;
    const norm = (h) => {
      const s = String(h || '').substring(0, 5);
      const [hh, mm] = s.split(':').map(Number);
      return (hh || 0) * 60 + (mm || 0);
    };
    const min = norm(horaStr);
    for (const t of turnos) {
      const start = norm(t.tca_hora_inicio);
      const end = norm(t.tca_hora_fin);
      if (t.tca_cruza_medianoche) {
        if (min >= start || min < end) return t.tca_id;
      } else {
        if (min >= start && min < end) return t.tca_id;
      }
    }
    return undefined;
  };

  const handleAbrirAgregarVenta = (destino, destinoKey) => {
    setDestinoParaVenta({ destino, destinoKey });
    setDetallesTemporal([]);
    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    setModalAgregarVentaVisible(true);
    loadArticulos();
  };

  const handleEliminarDetalleVenta = async (det, venta, destino, destinoKey) => {
    const vedId = det.ved_id;
    const venId = venta.ven_id;
    const articuloId = det.articulo_id;
    if ((!vedId && (!venId || !articuloId)) || eliminandoDetalleId) return;
    const idToShow = vedId ?? `${venId}-${articuloId}`;
    setEliminandoDetalleId(idToShow);
    try {
      const url = vedId
        ? `/gestion/ventas/detalles/${vedId}`
        : `/gestion/ventas/detalles/0?ven_id=${venId}&articulo_id=${articuloId}`;
      const res = await apiClient.delete(url);
      if (res.ok) {
        message.success('Artículo eliminado');
        const [tipo, destinoIdStr] = (destinoKey || '').split('-');
        const destinoId = parseInt(destinoIdStr, 10);
        await loadVentasPorDestino(tipo, destinoId);
        if (tipo === 'mesa') {
          await loadMesasParaCobros();
        } else {
          await loadDestinos();
        }
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = err?.error || err?.message || 'Error al eliminar el artículo';
        // Mostrar modal cuando el backend rechaza por validación (valor > total pendiente)
        if (res.status === 400) {
          Modal.error({
            title: 'No se puede eliminar',
            content: msg,
          });
        } else {
          message.error(msg);
        }
      }
    } catch (error) {
      console.error('Error al eliminar detalle:', error);
      message.error(error?.message || 'Error al eliminar el artículo');
    } finally {
      setEliminandoDetalleId(null);
    }
  };


  const handleAgregarDetalleCobros = () => {
    if (!articuloSeleccionado) {
      message.warning('Seleccione un artículo');
      return;
    }
    const articulo = articulos.find(a => a.art_id === articuloSeleccionado);
    if (!articulo) {
      message.error('Artículo no encontrado');
      return;
    }
    if (detallesTemporal.some(d => d.articulo_id === articuloSeleccionado)) {
      message.warning('Este artículo ya está en la lista');
      return;
    }
    if (articulo.art_mueve_stock && articulo.art_stock_actual < cantidadTemporal) {
      message.error(`Stock insuficiente. Disponible: ${articulo.art_stock_actual}`);
      return;
    }
    const precioUnitario = Number(articulo.art_precio_venta) || 0;
    setDetallesTemporal(prev => [...prev, {
      articulo_id: articulo.art_id,
      articulo_nombre: articulo.art_nombre,
      cantidad: cantidadTemporal,
      precio_unitario: precioUnitario,
      subtotal: cantidadTemporal * precioUnitario,
    }]);
    setArticuloSeleccionado(null);
    setCantidadTemporal(1);
    setTimeout(() => articuloSelectRef.current?.focus(), 0);
  };

  const handleQuitarDetalleCobros = (index) => {
    setDetallesTemporal(prev => prev.filter((_, i) => i !== index));
  };

  const handleGuardarVentaCobros = async () => {
    if (!destinoParaVenta) return;
    const { destino, destinoKey } = destinoParaVenta;
    if (detallesTemporal.length === 0) {
      message.error('Debe agregar al menos un artículo');
      return;
    }
    for (const detalle of detallesTemporal) {
      const articulo = articulos.find(a => a.art_id === detalle.articulo_id);
      if (articulo && articulo.art_mueve_stock && articulo.art_stock_actual < detalle.cantidad) {
        message.error(`Stock insuficiente para ${articulo.art_nombre}. Stock disponible: ${articulo.art_stock_actual}`);
        return;
      }
    }
    setLoadingAgregarVenta(true);
    try {
      let tipoDestino = 'mostrador';
      let canchaClubId = null;
      let turnoClubId = null;
      let mesaId = null;
      if (destino.tipo === 'cancha_turno') {
        tipoDestino = 'cancha';
        turnoClubId = destino.id;
      } else if (destino.tipo === 'cancha') {
        tipoDestino = 'cancha';
        canchaClubId = destino.id;
      } else if (destino.tipo === 'mesa') {
        tipoDestino = 'mesa';
        mesaId = destino.id;
      }
      const dataToSend = {
        fecha: fechaCobros,
        metodo_pago: 'efectivo',
        tipo_destino: tipoDestino,
        cancha_club_id: canchaClubId,
        turno_club_id: turnoClubId,
        mesa_id: mesaId,
        mesas_vinculadas: tipoDestino === 'mesa' && Array.isArray(mesasVinculadasSeleccionadas) ? mesasVinculadasSeleccionadas : undefined,
        detalles: detallesTemporal.map(d => ({
          articulo_id: d.articulo_id,
          cantidad: d.cantidad,
          precio_unitario: d.precio_unitario,
        })),
      };
      const response = await apiClient.post('/gestion/ventas', dataToSend);
      if (response.ok) {
        message.success('Venta registrada correctamente');
        setModalAgregarVentaVisible(false);
        setDestinoParaVenta(null);
        setDetallesTemporal([]);
        setArticuloSeleccionado(null);
        setCantidadTemporal(1);
        setMesasVinculadasSeleccionadas([]);
        const [tipo, destinoId] = destinoKey.split('-');
        await loadVentasPorDestino(tipo, parseInt(destinoId), { fecha: fechaCobros });
        if (tipo === 'mesa') await loadMesasParaCobros(); else await loadDestinos();
      } else {
        const errBody = await response.json().catch(() => ({}));
        message.error(errBody.message || errBody.error || 'Error al registrar la venta');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error(error?.message || 'Error al registrar la venta');
    } finally {
      setLoadingAgregarVenta(false);
    }
  };

  // Una venta es "ticket del turno" si tiene un detalle y el total es el precio del turno (o con descuento, entre 50% y 100%)
  const esVentaTicketTurno = (venta, precioTurno) => {
    if (precioTurno == null || !venta?.detalles?.length) return false;
    const total = parseFloat(venta.ven_total);
    return venta.detalles.length === 1 && (Math.abs(total - precioTurno) < 0.02 || (total <= precioTurno && total >= precioTurno * 0.5));
  };

  useEffect(() => {
    loadDestinos();
    loadFormasPago();
    loadJugadores();
    loadTurnos();
  }, []);

  useEffect(() => {
    if (vistaCobros === 'mesas') {
      loadMesasParaCobros();
    }
  }, [vistaCobros, fechaCobros]);

  const handleExpandDestino = (tipo, destinoId, expanded) => {
    setExpandedDestinos(prev => ({
      ...prev,
      [`${tipo}-${destinoId}`]: expanded
    }));
    
    if (expanded && !ventasPorDestino[`${tipo}-${destinoId}`]) {
      loadVentasPorDestino(tipo, destinoId);
    }
  };

  const handleRegistrarPago = async (ventaId, destinoKey) => {
    const monto = montosPago[ventaId];
    const formaPagoId = formasPagoSeleccionadas[ventaId];
    const formaPago = formasPago.find(fp => fp.fop_id === formaPagoId);
    const esCuentaCorriente = formaPago && formaPago.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
    const esEfectivo = formaPago && formaPago.fop_nombre?.toUpperCase().trim() === 'EFECTIVO';
    const jugadorId = jugadoresSeleccionados[ventaId];
    const descuentoPct = descuentosPorcentaje[ventaId] || 0;

    if (!monto || monto <= 0) {
      message.warning('Ingrese un monto válido');
      return;
    }

    if (!formaPagoId) {
      message.warning('Seleccione una forma de pago');
      return;
    }

    if (esCuentaCorriente && !jugadorId) {
      message.warning('Debe seleccionar un jugador para pagos en cuenta corriente');
      return;
    }

    setLoadingPago(prev => ({ ...prev, [ventaId]: true }));
    try {
      const response = await apiClient.post('/gestion/cobros/pago', {
        venta_id: ventaId,
        forma_pago_id: formaPagoId,
        monto: monto,
        jugador_club_id: esCuentaCorriente ? jugadorId : null,
        descuento_porcentaje: esEfectivo && descuentoPct > 0 ? descuentoPct : null,
      });

      if (response.ok) {
        message.success('Pago registrado correctamente');
        setMontosPago(prev => ({ ...prev, [ventaId]: 0 }));
        setFormasPagoSeleccionadas(prev => ({ ...prev, [ventaId]: null }));
        setJugadoresSeleccionados(prev => ({ ...prev, [ventaId]: null }));
        setDescuentosPorcentaje(prev => ({ ...prev, [ventaId]: 0 }));
        
        // Recargar ventas del destino
        const [tipo, destinoId] = destinoKey.split('-');
        const ventasActualizadas = await loadVentasPorDestino(tipo, parseInt(destinoId));
        
        // Recargar destinos/mesas para actualizar totales
        if (tipo === 'mesa') await loadMesasParaCobros(); else await loadDestinos();

        // Si el pago fue total (saldo pendiente = 0), contraer "Ver ventas" y cerrar drawer
        const totalPendienteRestante = (ventasActualizadas || []).reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
        if (totalPendienteRestante === 0) {
          setExpandedDestinos(prev => ({ ...prev, [destinoKey]: false }));
          setExpandedPanelsDestino(prev => ({ ...prev, [destinoKey]: [] }));
          setSelectedItem(null);
        }
      } else {
        const error = await response.json();
        message.error(error.message || 'Error al registrar el pago');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al registrar el pago');
    } finally {
      setLoadingPago(prev => ({ ...prev, [ventaId]: false }));
    }
  };

  const handleRegistrarPagoUnificado = async (destinoKey, ventas, destino) => {
    const ventaIds = ventas.map(v => v.ven_id);
    const formaPagoId = formaPagoUnificada[destinoKey];
    const formaPago = formasPago.find(fp => fp.fop_id === formaPagoId);
    const esCuentaCorriente = formaPago && formaPago.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
    const esEfectivo = formaPago && formaPago.fop_nombre?.toUpperCase().trim() === 'EFECTIVO';
    const jugadorId = jugadorUnificado[destinoKey];
    const descuentoPct = descuentoUnificado[destinoKey] || 0;
    const totalSoloVentas = ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
    const totalPendiente = destino?.tipo === 'cancha_turno' ? (Number(destino.total_pendiente) ?? totalSoloVentas) : totalSoloVentas;
    const montoIngresado = montoPagoUnificado[destinoKey] ?? totalPendiente;

    if (!formaPagoId) {
      message.warning('Seleccione una forma de pago');
      return;
    }
    if (esCuentaCorriente && !jugadorId) {
      message.warning('Debe seleccionar un jugador para pagos en cuenta corriente');
      return;
    }
    if (!montoIngresado || montoIngresado <= 0) {
      message.warning('Ingrese un monto válido');
      return;
    }
    if (montoIngresado > totalPendiente) {
      message.warning(`El monto no puede exceder el total pendiente ($${totalPendiente.toFixed(2)})`);
      return;
    }

    setLoadingPagoUnificado(prev => ({ ...prev, [destinoKey]: true }));
    try {
      const payload = {
        venta_ids: ventaIds,
        forma_pago_id: formaPagoId,
        jugador_club_id: esCuentaCorriente ? jugadorId : null,
        descuento_porcentaje: esEfectivo && descuentoPct > 0 ? descuentoPct : null,
        monto_total: montoIngresado,
        observaciones: `Cobro unificado ventas ${ventaIds.join(', ')}${montoIngresado < totalPendiente ? ` (pago parcial: $${montoIngresado.toFixed(2)} de $${totalPendiente.toFixed(2)})` : ''}`,
      };
      if (destino && destino.tipo === 'cancha_turno') {
        payload.turno_club_id = destino.id;
      }
      const response = await apiClient.post('/gestion/cobros/pagos-multiples', payload);
      if (response.ok) {
        const data = await response.json();
        message.success(data.message || 'Pagos registrados correctamente');
        setMontoPagoUnificado(prev => ({ ...prev, [destinoKey]: 0 }));
        setFormaPagoUnificada(prev => ({ ...prev, [destinoKey]: null }));
        setDescuentoUnificado(prev => ({ ...prev, [destinoKey]: 0 }));
        setJugadorUnificado(prev => ({ ...prev, [destinoKey]: null }));
        const [tipo, destinoId] = destinoKey.split('-');
        const ventasActualizadas = await loadVentasPorDestino(tipo, parseInt(destinoId));
        if (tipo === 'mesa') await loadMesasParaCobros(); else await loadDestinos();

        // Si el pago fue total (saldo pendiente = 0), contraer "Ver ventas" y cerrar drawer
        const totalPendienteRestante = (ventasActualizadas || []).reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
        if (totalPendienteRestante === 0) {
          setExpandedDestinos(prev => ({ ...prev, [destinoKey]: false }));
          setExpandedPanelsDestino(prev => ({ ...prev, [destinoKey]: [] }));
          setSelectedItem(null);
        }
      } else {
        const err = await response.json();
        message.error(err.message || 'Error al registrar los pagos');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al registrar los pagos');
    } finally {
      setLoadingPagoUnificado(prev => ({ ...prev, [destinoKey]: false }));
    }
  };

  const handleCobrarTurno = async (destino) => {
    const destinoKey = `${destino.tipo}-${destino.id}`;
    const monto = montoPagoUnificado[destinoKey] ?? destino.total_pendiente;
    const formaPagoId = formaPagoUnificada[destinoKey];
    const formaPago = formasPago.find(fp => fp.fop_id === formaPagoId);
    const esCuentaCorriente = formaPago && formaPago.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE';
    const jugadorId = jugadorUnificado[destinoKey];

    if (!monto || monto <= 0) {
      message.warning('Ingrese un monto válido');
      return;
    }
    if (!formaPagoId) {
      message.warning('Seleccione una forma de pago');
      return;
    }
    if (esCuentaCorriente && !jugadorId) {
      message.warning('Debe seleccionar un jugador para pagos en cuenta corriente');
      return;
    }

    setLoadingPagoUnificado(prev => ({ ...prev, [destinoKey]: true }));
    try {
      const response = await apiClient.post('/gestion/cobros/cobrar-turno', {
        turno_club_id: destino.id,
        monto: Number(monto),
        forma_pago_id: formaPagoId,
        jugador_club_id: esCuentaCorriente ? jugadorId : null,
        descuento_porcentaje: (descuentoUnificado[destinoKey] || 0) > 0 ? descuentoUnificado[destinoKey] : null,
      });
      if (response.ok) {
        message.success('Turno cobrado correctamente');
        setMontoPagoUnificado(prev => ({ ...prev, [destinoKey]: 0 }));
        setFormaPagoUnificada(prev => ({ ...prev, [destinoKey]: null }));
        setDescuentoUnificado(prev => ({ ...prev, [destinoKey]: 0 }));
        setJugadorUnificado(prev => ({ ...prev, [destinoKey]: null }));
        await loadVentasPorDestino(destino.tipo, destino.id);
        await loadDestinos();
        // Pago total: contraer paneles y cerrar drawer
        setExpandedDestinos(prev => ({ ...prev, [destinoKey]: false }));
        setExpandedPanelsDestino(prev => ({ ...prev, [destinoKey]: [] }));
        setSelectedItem(null);
      } else {
        const err = await response.json().catch(() => ({}));
        message.error(err.message || 'Error al cobrar el turno');
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('Error al cobrar el turno');
    } finally {
      setLoadingPagoUnificado(prev => ({ ...prev, [destinoKey]: false }));
    }
  };

  /** Mismo criterio que el backend (cobros.service registrarPagosMultiplesVentas / cobrar turno) */
  const montoACobrarConDescuentoEfectivo = (montoBase, porcentajeDescuento) => {
    const b = Number(montoBase) || 0;
    const p = Math.min(100, Math.max(0, Number(porcentajeDescuento) || 0));
    if (p <= 0) return Math.round(b * 100) / 100;
    return Math.round(b * (1 - p / 100) * 100) / 100;
  };

  const columnsVentas = [
    {
      title: 'Fecha',
      dataIndex: 'ven_fecha',
      key: 'ven_fecha',
      width: 100,
      render: (fecha) => dayjs(fecha).format('DD/MM/YYYY'),
    },
    {
      title: 'Detalles',
      key: 'detalles',
      render: (_, record) => (
        <div>
          {record.detalles && record.detalles.length > 0 ? (
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {record.detalles.map((det, idx) => (
                <li key={idx} style={{ color: '#e0e0e0' }}>
                  {det.articulo_nombre} x{det.cantidad} - ${parseFloat(det.subtotal).toFixed(2)}
                </li>
              ))}
            </ul>
          ) : (
            <Text style={{ color: '#b0b0b0' }}>-</Text>
          )}
        </div>
      ),
    },
    {
      title: 'Total',
      dataIndex: 'ven_total',
      key: 'ven_total',
      width: 100,
      align: 'right',
      render: (total) => (
        <Text strong style={{ color: '#ffffff' }}>
          ${parseFloat(total).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Pagado',
      dataIndex: 'total_pagado',
      key: 'total_pagado',
      width: 100,
      align: 'right',
      render: (pagado) => (
        <Text style={{ color: '#52c41a' }}>
          ${parseFloat(pagado || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: 'Pendiente',
      dataIndex: 'saldo_pendiente',
      key: 'saldo_pendiente',
      width: 100,
      align: 'right',
      render: (pendiente) => (
        <Text strong style={{ color: '#ff4d4f' }}>
          ${parseFloat(pendiente || 0).toFixed(2)}
        </Text>
      ),
    },
  ];

  const getTurnoCajaSeleccionado = (destino, destinoKey, ventas) => {
    return (
      (ventas.length > 0 && ventas[0]?.ven_turno_caja_id != null ? ventas[0].ven_turno_caja_id : null) ??
      (destino.tipo === 'cancha_turno' ? getTurnoCajaPorHora(destino.hora_desde) : undefined) ??
      (destino.tipo === 'mesa' ? getTurnoCajaPorHora(dayjs().format('HH:mm')) : undefined) ??
      destino.turno_caja_id ??
      null
    );
  };

  const ordenTipoDestino = (tipo) => {
    if (tipo === 'cancha_turno') return 1;
    if (tipo === 'cancha') return 2;
    return 3;
  };

  const itemsCobros = [];
  if (destinos && destinos.length > 0) {
    const destinosEnriquecidos = destinos
      .map((destino) => {
        const destinoKey = `${destino.tipo}-${destino.id}`;
        const ventas = ventasPorDestino[destinoKey] || [];
        const turnoSeleccionado = getTurnoCajaSeleccionado(destino, destinoKey, ventas);
        return { destino, destinoKey, ventas, turnoSeleccionado };
      })
      .sort((a, b) => {
        const ta = a.turnoSeleccionado ?? 0;
        const tb = b.turnoSeleccionado ?? 0;
        if (ta !== tb) return ta - tb;
        const oa = ordenTipoDestino(a.destino.tipo);
        const ob = ordenTipoDestino(b.destino.tipo);
        if (oa !== ob) return oa - ob;
        return String(a.destino.nombre || '').localeCompare(String(b.destino.nombre || ''));
      });

    let ultimoTurnoId = undefined;
    destinosEnriquecidos.forEach((item) => {
      const turnoId = item.turnoSeleccionado ?? null;
      if (turnoId !== ultimoTurnoId) {
        itemsCobros.push({ type: 'header', turnoId });
        ultimoTurnoId = turnoId;
      }
      itemsCobros.push({ type: 'destino', ...item });
    });
  }

  const extraerGrupoCancha = (nombre) => {
    if (!nombre || typeof nombre !== 'string') return 'Otros';
    const trimmed = nombre.trim();
    const matchCancha = trimmed.match(/^(Cancha\s+\S+)/i);
    if (matchCancha) return matchCancha[1].trim();
    if (trimmed.toLowerCase().startsWith('mesa')) return 'Mesas';
    const primeraPalabra = trimmed.split(/\s+/)[0];
    if (primeraPalabra) return primeraPalabra;
    return 'Otros';
  };

  const itemsPorCancha = useMemo(() => {
    const destinosItems = (itemsCobros || []).filter((i) => i.type === 'destino');
    const grupos = {};
    destinosItems.forEach((item) => {
      const grupo = extraerGrupoCancha(item.destino?.nombre);
      if (!grupos[grupo]) grupos[grupo] = [];
      grupos[grupo].push(item);
    });
    const ordenCancha = (a, b) => {
      const esCanchaA = /^Cancha\s+/i.test(a);
      const esCanchaB = /^Cancha\s+/i.test(b);
      if (esCanchaA && !esCanchaB) return -1;
      if (!esCanchaA && esCanchaB) return 1;
      if (esCanchaA && esCanchaB) {
        const numA = parseInt((a.match(/\d+/) || [0])[0], 10) || 0;
        const numB = parseInt((b.match(/\d+/) || [0])[0], 10) || 0;
        return numA - numB;
      }
      if (a === 'Mesas') return 1;
      if (b === 'Mesas') return -1;
      if (a === 'Otros') return 1;
      if (b === 'Otros') return -1;
      return a.localeCompare(b);
    };
    const keys = Object.keys(grupos).sort(ordenCancha);
    return keys.map((k) => ({ key: k, label: k, items: grupos[k] }));
  }, [itemsCobros]);

  const itemsFiltradosPorVista = useMemo(() => {
    if (vistaCobros === 'canchas') {
      return itemsPorCancha.filter((tab) => tab.key !== 'Mesas');
    }
    return itemsPorCancha.filter((tab) => tab.key === 'Mesas');
  }, [itemsPorCancha, vistaCobros]);

  return (
    <MainLayout>
      <div style={{ padding: '24px' }}>
        <div style={{ marginBottom: 16, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <h1 style={{ margin: 0, color: '#ffffff', fontSize: isMobile ? '20px' : '24px' }}>
              Cobros
            </h1>
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <Button
                size="small"
                type={vistaCobros === 'canchas' ? 'primary' : 'default'}
                onClick={() => setVistaCobros('canchas')}
                style={{ width: 104 }}
              >
                Canchas
              </Button>
              <Button
                size="small"
                type={vistaCobros === 'mesas' ? 'primary' : 'default'}
                onClick={() => setVistaCobros('mesas')}
                style={{ width: 104 }}
              >
                Mesas
              </Button>
            </div>
          </div>
          <Space wrap>
            <span style={{ color: '#b0b0b0', fontSize: 13 }}>Día:</span>
            <DatePicker
              value={fechaCobros ? dayjs(fechaCobros) : null}
              onChange={(date) => {
                const f = date ? dayjs(date).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD');
                setFechaCobros(f);
                loadDestinos(f);
                if (vistaCobros === 'mesas') loadMesasParaCobros(f);
              }}
              format="DD/MM/YYYY"
              allowClear={false}
            />
            <Button
              icon={<ReloadOutlined />}
              onClick={() => {
                loadDestinos();
                if (vistaCobros === 'mesas') loadMesasParaCobros();
              }}
              loading={vistaCobros === 'mesas' ? loadingMesas : loading}
            >
              Actualizar
            </Button>
          </Space>
        </div>

        {vistaCobros === 'mesas' ? (
          mesasParaCobros.length === 0 && !loadingMesas ? (
            <Card
              style={{
                backgroundColor: '#2d2d2d',
                border: '1px solid #434343',
                borderRadius: 8,
              }}
            >
              <Empty
                description="No hay mesas configuradas. Configurá mesas en Ajustes → Mesas."
                style={{ color: '#b0b0b0' }}
              />
            </Card>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fill, minmax(140px, 1fr))',
                gap: 16,
                padding: '12px 0',
              }}
            >
              {mesasParaCobros.map((mesa) => {
                const destinoKey = `mesa-${mesa.id}`;
                const pendienteNum = parseFloat(mesa.total_pendiente) || 0;
                const estaLibre = mesa.total_consumido === 0 || pendienteNum <= 0;
                const estaOcupado = mesa.total_consumido > 0 && pendienteNum > 0;
                const responsableMostrar = pendienteNum > 0 ? (mesa.responsable || null) : null;
                const item = { destino: mesa, destinoKey, ventas: ventasPorDestino[destinoKey] ?? [] };
                return (
                  <div
                    key={destinoKey}
                    onClick={() => {
                      setSelectedItem(item);
                      if (!ventasPorDestino[destinoKey]) loadVentasPorDestino('mesa', mesa.id);
                    }}
                    style={{
                      aspectRatio: '1',
                      minHeight: 100,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 12,
                      borderRadius: 12,
                      backgroundColor: mesa.es_vinculada ? '#1e3a3a' : '#2d2d2d',
                      border: `2px solid ${mesa.es_vinculada ? '#13c2c2' : '#434343'}`,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative',
                      boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.03)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = mesa.es_vinculada ? '#36cfc9' : '#722ed1';
                      e.currentTarget.style.backgroundColor = mesa.es_vinculada ? '#2a4a4a' : '#3a3a3a';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = mesa.es_vinculada ? '#13c2c2' : '#434343';
                      e.currentTarget.style.backgroundColor = mesa.es_vinculada ? '#1e3a3a' : '#2d2d2d';
                    }}
                  >
                    {(estaLibre || estaOcupado) && (
                      <Tag
                        color={estaLibre ? 'green' : 'orange'}
                        style={{ position: 'absolute', top: 6, right: 6, margin: 0, fontSize: 10 }}
                      >
                        {estaLibre ? 'Libre' : 'Ocupado'}
                      </Tag>
                    )}
                    <TableOutlined style={{ fontSize: 24, color: mesa.es_vinculada ? '#13c2c2' : '#722ed1', marginBottom: 6 }} />
                    <Text strong style={{ color: '#e0e0e0', fontSize: 14, textAlign: 'center' }}>
                      {mesa.nombre}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 11, color: responsableMostrar ? '#b0b0b0' : '#666', textAlign: 'center', marginTop: 2, display: 'block', fontStyle: responsableMostrar ? 'normal' : 'italic' }}>
                      {responsableMostrar || 'Sin asignar'}
                    </Text>
                    {mesa.es_vinculada && mesa.mesa_principal_id && (
                      <Text type="secondary" style={{ fontSize: 10, color: '#13c2c2', textAlign: 'center', marginTop: 2 }}>
                        rel. {mesasParaCobros.find(m => m.id === mesa.mesa_principal_id)?.nombre || `Mesa ${mesa.mesa_principal_id}`}
                      </Text>
                    )}
                    {mesa.mesas_vinculadas?.length > 0 && (
                      <Text type="secondary" style={{ fontSize: 10, color: '#722ed1', textAlign: 'center', marginTop: 2 }}>
                        rel. {mesa.mesas_vinculadas.map(id => mesasParaCobros.find(m => m.id === id)?.nombre || `Mesa ${id}`).join(', ')}
                      </Text>
                    )}
                    <Text style={{ color: '#b0b0b0', fontSize: 11, marginTop: 4 }}>
                      ${(estaLibre ? 0 : mesa.total_consumido).toFixed(2)}
                    </Text>
                    {mesa.total_pendiente > 0 && (
                      <Text style={{ color: '#ff4d4f', fontSize: 11 }}>Pend: ${mesa.total_pendiente.toFixed(2)}</Text>
                    )}
                  </div>
                );
              })}
            </div>
          )
        ) : destinos.length === 0 && !loading ? (
          <Card
            style={{
              backgroundColor: '#2d2d2d',
              border: '1px solid #434343',
              borderRadius: 8,
            }}
          >
            <Empty
              description="No hay turnos reservados ni canchas con ventas pendientes para este día"
              style={{ color: '#b0b0b0' }}
            />
          </Card>
        ) : itemsFiltradosPorVista.length === 0 ? (
          <Card
            style={{
              backgroundColor: '#2d2d2d',
              border: '1px solid #434343',
              borderRadius: 8,
            }}
          >
            <Empty
              description="No hay canchas con cobros pendientes para este día"
              style={{ color: '#b0b0b0' }}
            />
          </Card>
        ) : (
          <>
            <Tabs
              key={vistaCobros}
              defaultActiveKey={itemsFiltradosPorVista[0]?.key}
              items={itemsFiltradosPorVista.map((tab) => ({
                key: tab.key,
                label: tab.label,
                children: (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(220px, 1fr))',
                      gap: 10,
                      padding: '12px 0',
                    }}
                  >
                    {tab.items.map((item) => {
                      const destino = item.destino;
                      const destinoKey = item.destinoKey;
                      const estaPagado = destino.total_pendiente === 0;
                      return (
                        <div
                          key={destinoKey}
                          onClick={() => setSelectedItem(item)}
                          style={{
                            position: 'relative',
                            padding: '14px 16px',
                            borderRadius: 12,
                            backgroundColor: '#2d2d2d',
                            border: '1px solid #434343',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#1890ff';
                            e.currentTarget.style.backgroundColor = '#3a3a3a';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#434343';
                            e.currentTarget.style.backgroundColor = '#2d2d2d';
                          }}
                        >
                          {estaPagado && (
                            <Tag color="green" style={{ position: 'absolute', top: 8, right: 8, margin: 0 }}>
                              PAGADO
                            </Tag>
                          )}
                          {!estaPagado && (destino.total_pendiente ?? 0) > 0 && (
                            <Tag color="red" style={{ position: 'absolute', top: 8, right: 8, margin: 0 }}>
                              DEBE
                            </Tag>
                          )}
                          {(() => {
                            const parts = (destino.nombre || '').split(' - ');
                            const lineaArriba = parts.length >= 2 ? `${parts[0]} - ${parts[1]}` : destino.nombre || '';
                            const lineaAbajo = parts.length >= 3 ? parts.slice(2).join(' - ') : '';
                            return (
                              <>
                                <Text style={{ color: '#e0e0e0', fontSize: 13, display: 'block', fontWeight: 500 }}>
                                  {lineaArriba}
                                </Text>
                                {lineaAbajo && (
                                  <Text style={{ color: '#b0b0b0', fontSize: 12, display: 'block', marginTop: 4 }}>
                                    {lineaAbajo}
                                  </Text>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      );
                    })}
                  </div>
                ),
              }))}
            />
          </>
        )}

        <Drawer
          title={selectedItem?.destino?.nombre}
          placement="right"
          width={Math.min(480, typeof window !== 'undefined' ? window.innerWidth - 32 : 480)}
          onClose={() => setSelectedItem(null)}
          open={!!selectedItem}
          styles={{
            body: { backgroundColor: '#1a1a1a', padding: 16 },
            header: { backgroundColor: '#2d2d2d', borderBottom: '1px solid #434343', color: '#fff' },
          }}
        >
              {selectedItem && (() => {
                const destinoActualizado = selectedItem.destino?.tipo === 'mesa'
                  ? (mesasParaCobros.find((m) => m.id === selectedItem.destino?.id) ?? selectedItem.destino)
                  : (destinos.find((d) => d.tipo === selectedItem.destino?.tipo && d.id === selectedItem.destino?.id) ?? selectedItem.destino);
                const destinoKey = selectedItem.destinoKey;
                const ventasItem = ventasPorDestino[destinoKey] ?? selectedItem.ventas;
                const item = {
                  ...selectedItem,
                  destino: destinoActualizado,
                  destinoKey,
                  ventas: ventasItem,
                  turnoSeleccionado: getTurnoCajaSeleccionado(destinoActualizado, destinoKey, ventasItem),
                };
                const destino = item.destino;
                const ventas = item.ventas;
                const estaPagado = destino.total_pendiente === 0;
                // Para mesas no mostramos "PAGADO" cruzado: cuando pagan queda Libre para nuevo consumo
                const mostrarOverlayPagado = estaPagado && destino.tipo !== 'mesa';
                // Mesa Libre (pendiente=0): mostramos ceros para indicar que está lista para nuevo consumo
                const mesaLibre = destino.tipo === 'mesa' && destino.total_pendiente === 0;
                const totalConsumidoMostrar = mesaLibre ? 0 : (destino.total_consumido ?? 0);
                const totalPagadoMostrar = mesaLibre ? 0 : (destino.total_pagado ?? 0);
                // Agrupar pagos unificados (cobro único dividido en varios registros por venta)
                const todosPagos = ventas.flatMap(v => (v.pagos || []).map(p => ({ ...p, ven_id: v.ven_id })));
                const pagosOrdenados = [...todosPagos].sort((a, b) => new Date(b.fecha || 0) - new Date(a.fecha || 0));
                const gruposPagos = (() => {
                  const grupos = [];
                  const usados = new Set();
                  for (let i = 0; i < pagosOrdenados.length; i++) {
                    if (usados.has(i)) continue;
                    const p = pagosOrdenados[i];
                    const obs = (p.observaciones || '').trim();
                    const esUnificado = obs.includes('Cobro unificado') || obs.includes('unificado ventas');
                    const grupo = [p];
                    usados.add(i);
                    if (esUnificado && obs) {
                      const fechaP = dayjs(p.fecha).startOf('minute').valueOf();
                      const formaP = p.forma_pago || '';
                      for (let j = i + 1; j < pagosOrdenados.length; j++) {
                        if (usados.has(j)) continue;
                        const q = pagosOrdenados[j];
                        if ((q.observaciones || '').trim() === obs && (q.forma_pago || '') === formaP &&
                            Math.abs(dayjs(q.fecha).startOf('minute').valueOf() - fechaP) < 60000) {
                          grupo.push(q);
                          usados.add(j);
                        }
                      }
                    }
                    grupos.push(grupo);
                  }
                  return grupos;
                })();
                return (
                <div style={{ position: 'relative' }}>
                  {mostrarOverlayPagado && (
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        pointerEvents: 'none',
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          width: '140%',
                          height: 2,
                          backgroundColor: 'rgba(82, 196, 26, 0.7)',
                          transform: 'rotate(-45deg)',
                          top: '35%',
                          left: '-20%',
                        }}
                      />
                      <div
                        style={{
                          position: 'absolute',
                          width: '140%',
                          height: 2,
                          backgroundColor: 'rgba(82, 196, 26, 0.7)',
                          transform: 'rotate(-45deg)',
                          top: '60%',
                          left: '-20%',
                        }}
                      />
                      <span
                        style={{
                          position: 'relative',
                          zIndex: 2,
                          fontSize: 28,
                          fontWeight: 'bold',
                          color: 'rgba(82, 196, 26, 0.95)',
                          textShadow: '0 0 8px rgba(0,0,0,0.8)',
                          transform: 'rotate(-45deg)',
                          letterSpacing: 2,
                        }}
                      >
                        PAGADO
                      </span>
                    </div>
                  )}
                <Card
                  size="small"
                  style={{
                    backgroundColor: '#2d2d2d',
                    border: '1px solid #434343',
                    borderRadius: 8,
                  }}
                  headStyle={{
                    backgroundColor: '#2d2d2d',
                    borderBottom: '1px solid #434343',
                    color: '#ffffff',
                    padding: '8px 12px',
                  }}
                  bodyStyle={{
                    backgroundColor: '#2d2d2d',
                    color: '#e0e0e0',
                    padding: 12,
                  }}
                  title={
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {destino.tipo === 'cancha_turno' ? (
                          <ClockCircleOutlined style={{ color: '#1890ff' }} />
                        ) : destino.tipo === 'cancha' ? (
                          <EnvironmentOutlined style={{ color: '#1890ff' }} />
                        ) : (
                          <TableOutlined style={{ color: destino.es_vinculada ? '#13c2c2' : '#722ed1' }} />
                        )}
                        <span style={{ color: '#ffffff', fontWeight: 'bold', fontSize: 13 }}>
                          {destino.nombre}
                        </span>
                        <Tag color={destino.tipo === 'cancha_turno' ? 'blue' : destino.tipo === 'cancha' ? 'blue' : destino.es_vinculada ? 'cyan' : 'purple'}>
                          {destino.tipo === 'cancha_turno' ? 'Turno' : destino.tipo === 'cancha' ? 'Cancha' : destino.es_vinculada ? 'Mesa vinculada' : 'Mesa'}
                        </Tag>
                      </div>
                      {destino.tipo === 'mesa' && (destino.es_vinculada ? (
                        <Text style={{ color: '#13c2c2', fontSize: 11, marginTop: 2 }}>
                          Relacionada con {mesasParaCobros.find(m => m.id === destino.mesa_principal_id)?.nombre || `Mesa ${destino.mesa_principal_id}`}. Los gastos se cargan en la mesa principal.
                        </Text>
                      ) : destino.mesas_vinculadas?.length > 0 ? (
                        <Text style={{ color: '#722ed1', fontSize: 11, marginTop: 2 }}>
                          Relacionada con {destino.mesas_vinculadas.map(id => mesasParaCobros.find(m => m.id === id)?.nombre || `Mesa ${id}`).join(', ')}
                        </Text>
                      ) : null)}
                    </div>
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={8}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ flex: '1 1 0', minWidth: 80 }}>
                        <Text style={{ color: '#b0b0b0', display: 'block', fontSize: 11 }}>
                          Total consumido
                        </Text>
                        <Text strong style={{ color: '#ffffff', fontSize: 15 }}>
                          ${totalConsumidoMostrar.toFixed(2)}
                        </Text>
                      </div>
                      <div style={{ flex: '1 1 0', minWidth: 80 }}>
                        <Text style={{ color: '#b0b0b0', display: 'block', fontSize: 11 }}>
                          Total pagado
                        </Text>
                        <Text strong style={{ color: '#52c41a', fontSize: 15 }}>
                          ${totalPagadoMostrar.toFixed(2)}
                        </Text>
                      </div>
                      <div style={{ flex: '1 1 0', minWidth: 80 }}>
                        <Text style={{ color: '#b0b0b0', display: 'block', fontSize: 11 }}>
                          Total pendiente
                        </Text>
                        <Text strong style={{ color: '#ff4d4f', fontSize: 15 }}>
                          ${destino.total_pendiente.toFixed(2)}
                        </Text>
                      </div>
                    </div>

                    <Collapse
                      ghost
                      activeKey={expandedPanelsDestino[destinoKey] || []}
                      onChange={(keys) => setExpandedPanelsDestino(prev => ({ ...prev, [destinoKey]: keys }))}
                    >
                      {destino.tipo === 'cancha_turno' && destino.precio_turno != null && (
                        <Panel
                          header={<Text style={{ color: '#ffffff' }}>Turno</Text>}
                          key="turno"
                        >
                          <div style={{ padding: '8px 0' }}>
                            <Text style={{ color: '#b0b0b0', display: 'block' }}>
                              Precio del turno: <Text strong style={{ color: '#ffffff' }}>${(destino.precio_turno ?? destino.total_consumido).toFixed(2)}</Text>
                            </Text>
                            {(destino.senia_pagada ?? destino.total_pagado ?? 0) > 0 && (
                              <div style={{ marginTop: 4 }}>
                                <Text style={{ color: '#b0b0b0' }}>Seña entregada (reserva): </Text>
                                <Text style={{ color: '#52c41a' }}>${(destino.senia_pagada ?? destino.total_pagado ?? 0).toFixed(2)}</Text>
                              </div>
                            )}
                          </div>
                        </Panel>
                      )}
                      <Panel
                        header={
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                            <Text style={{ color: '#ffffff' }}>
                              Ventas ({(() => {
                                const soloConsumo = destino.tipo === 'cancha_turno' ? ventas.filter(v => !esVentaTicketTurno(v, destino.precio_turno)) : ventas;
                                return soloConsumo.length;
                              })()})
                            </Text>
                            {!(destino.tipo === 'mesa' && destino.es_vinculada) && (
                              <Button
                                type="primary"
                                size="small"
                                icon={<PlusOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleAbrirAgregarVenta(destino, destinoKey);
                                }}
                              >
                                Agregar venta
                              </Button>
                            )}
                          </div>
                        }
                        key="ventas"
                      >
                        {ventas.length > 0 ? (
                          <div>
                            {(() => {
                              const ventasSoloConsumo = destino.tipo === 'cancha_turno' ? ventas.filter(v => !esVentaTicketTurno(v, destino.precio_turno)) : ventas;
                              if (ventasSoloConsumo.length === 0) {
                                return (
                                  <div style={{ textAlign: 'center', padding: '20px 0', color: '#b0b0b0' }}>
                                    No hay ventas de consumo
                                  </div>
                                );
                              }
                              return (
                                <div style={{ padding: '4px 0' }}>
                                  {ventasSoloConsumo.map((venta) => (
                                    <div key={venta.ven_id} style={{ marginBottom: 12 }}>
                                      <Text strong style={{ color: '#1890ff', display: 'block', marginBottom: 6 }}>
                                        {`Consumo Venta #${venta.ven_id}`} ({dayjs(venta.ven_fecha).format('DD/MM/YYYY')})
                                      </Text>
                                      {venta.detalles && venta.detalles.length > 0 ? (
                                        <div style={{ marginLeft: 12 }}>
                                          {venta.detalles.map((det, idx) => (
                                            <div key={det.ved_id ?? idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, color: '#b0b0b0' }}>
                                              <span>• {det.articulo_nombre} x{det.cantidad} - ${parseFloat(det.subtotal).toFixed(2)}</span>
                                              {hasPermission('ventas', 'eliminar') && (det.ved_id || det.articulo_id) && (
                                                <Button
                                                  type="text"
                                                  size="small"
                                                  danger
                                                  icon={<DeleteOutlined />}
                                                  loading={eliminandoDetalleId === (det.ved_id ?? `${venta.ven_id}-${det.articulo_id}`)}
                                                  onClick={() => handleEliminarDetalleVenta(det, venta, destino, destinoKey)}
                                                  title="Eliminar este artículo"
                                                />
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <Text style={{ color: '#b0b0b0', marginLeft: 12 }}>-</Text>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              );
                            })()}
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '20px 0', color: '#b0b0b0' }}>
                            {ventasPorDestino.hasOwnProperty(destinoKey) ? 'No hay ventas' : 'Cargando ventas...'}
                          </div>
                        )}
                      </Panel>
                      <Panel
                        header={
                          <Text style={{ color: '#ffffff' }}>
                            Pagos ({gruposPagos.length})
                          </Text>
                        }
                        key="pagos"
                      >
                        {gruposPagos.length === 0 ? (
                          <div style={{ textAlign: 'center', padding: '20px 0', color: '#b0b0b0' }}>
                            No hay pagos registrados
                          </div>
                        ) : (
                          <div style={{ padding: '4px 0' }}>
                            {gruposPagos.map((grupo, idx) => {
                              const primero = grupo[0];
                              const montoTotal = grupo.reduce((s, g) => s + parseFloat(g.monto || 0), 0);
                              const ventasIds = [...new Set(grupo.map(g => g.ven_id).filter(Boolean))].sort((a, b) => a - b);
                              return (
                                <div key={primero.id || idx} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: idx < gruposPagos.length - 1 ? '1px solid #434343' : 'none' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 4 }}>
                                    <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
                                      ${montoTotal.toFixed(2)}
                                    </Text>
                                    <Tag color="blue">{primero.forma_pago || '—'}</Tag>
                                  </div>
                                  <Text style={{ color: '#888', fontSize: 11, display: 'block', marginTop: 4 }}>
                                    {dayjs(primero.fecha).format('DD/MM/YYYY HH:mm')}
                                    {ventasIds.length > 0 && ` · Ventas #${ventasIds.join(', #')}`}
                                  </Text>
                                  {primero.observaciones && (
                                    <Text style={{ color: '#b0b0b0', fontSize: 11, display: 'block', marginTop: 2 }}>
                                      {primero.observaciones}
                                    </Text>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </Panel>
                    </Collapse>
                    {destino.tipo === 'mesa' && !destino.es_vinculada && (parseFloat(destino.total_pendiente) || 0) > 0 && (
                      <div style={{ marginTop: 16, padding: '12px 0', borderTop: '1px solid #434343' }}>
                        <Text style={{ color: '#b0b0b0', display: 'block', fontSize: 12, marginBottom: 6 }}>Responsable de la mesa</Text>
                        <Input
                          placeholder="Nombre del responsable (ej. mozo, camarero)"
                          value={responsableMesa[destino.id] ?? destino.responsable ?? ''}
                          onChange={(e) => setResponsableMesa(prev => ({ ...prev, [destino.id]: e.target.value }))}
                          onBlur={async () => {
                            const valor = responsableMesa[destino.id] ?? destino.responsable ?? '';
                            try {
                              const res = await apiClient.put(`/gestion/cobros/mesas/${destino.id}/responsable`, { responsable: valor || null });
                              if (res.ok) {
                                const responsableActualizado = valor || null;
                                setMesasParaCobros(prev => prev.map(m => m.id === destino.id ? { ...m, responsable: responsableActualizado } : m));
                                setSelectedItem(prev => prev && prev.destino?.id === destino.id ? { ...prev, destino: { ...prev.destino, responsable: responsableActualizado } } : prev);
                                setResponsableMesa(prev => { const n = { ...prev }; delete n[destino.id]; return n; });
                                message.success('Responsable guardado');
                                await loadMesasParaCobros();
                              } else {
                                const err = await res.json().catch(() => ({}));
                                message.error(err?.message || 'Error al guardar');
                              }
                            } catch (e) {
                              message.error('Error al guardar el responsable');
                            }
                          }}
                          style={{ backgroundColor: '#2d2d2d', color: '#ffffff', borderColor: '#434343' }}
                        />
                      </div>
                    )}
                    {!(destino.tipo === 'mesa' && destino.es_vinculada) && hasPermission('cobros', 'crear') && ((ventas.length > 0 && (destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0)) > 0) || (ventas.length === 0 && destino.tipo === 'cancha_turno' && destino.total_pendiente > 0)) ? (
                      <div style={{ marginTop: 16, padding: '16px 0', borderTop: '1px solid #434343' }}>
                        <Form layout="vertical" size="small">
                          <Form.Item label={<Text style={{ color: '#ffffff' }}>Monto a pagar</Text>}>
                            <InputNumber
                              style={{ width: '100%' }}
                              placeholder={`Máximo: $${(destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0)).toFixed(2)}`}
                              value={montoPagoUnificado[destinoKey] ?? (destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0))}
                              onChange={(val) => {
                                const numValue = val ?? 0;
                                const saldoMax = destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
                                if (numValue > saldoMax) {
                                  setMontoPagoUnificado(prev => ({ ...prev, [destinoKey]: saldoMax }));
                                  message.warning(`El monto se ha ajustado al máximo: $${saldoMax.toFixed(2)}`);
                                } else {
                                  setMontoPagoUnificado(prev => ({ ...prev, [destinoKey]: numValue }));
                                }
                              }}
                              min={0}
                              max={destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0)}
                              precision={2}
                              prefix="$"
                            />
                          </Form.Item>
                          <Form.Item label={<Text style={{ color: '#ffffff' }}>Forma de pago</Text>} required>
                            <Select
                              placeholder="Seleccione forma de pago"
                              value={formaPagoUnificada[destinoKey]}
                              onChange={(v) => {
                                setFormaPagoUnificada(prev => ({ ...prev, [destinoKey]: v }));
                                if (formasPago.find(fp => fp.fop_id === v)?.fop_nombre?.toUpperCase().trim() !== 'CUENTA CORRIENTE') {
                                  setJugadorUnificado(prev => ({ ...prev, [destinoKey]: null }));
                                }
                                if (formasPago.find(fp => fp.fop_id === v)?.fop_nombre?.toUpperCase().trim() !== 'EFECTIVO') {
                                  setDescuentoUnificado(prev => ({ ...prev, [destinoKey]: 0 }));
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              {formasPago.map(fp => (
                                <Select.Option key={fp.fop_id} value={fp.fop_id}>{fp.fop_nombre}</Select.Option>
                              ))}
                            </Select>
                          </Form.Item>
                          {formaPagoUnificada[destinoKey] && formasPago.find(fp => fp.fop_id === formaPagoUnificada[destinoKey])?.fop_nombre?.toUpperCase().trim() === 'EFECTIVO' && (
                            <Form.Item label={<Text style={{ color: '#ffffff' }}>Descuento %</Text>}>
                              <InputNumber
                                min={0}
                                max={100}
                                value={descuentoUnificado[destinoKey] ?? 0}
                                onChange={(v) => setDescuentoUnificado(prev => ({ ...prev, [destinoKey]: v ?? 0 }))}
                                style={{ width: '100%' }}
                                addonAfter="%"
                                precision={1}
                              />
                            </Form.Item>
                          )}
                          {formaPagoUnificada[destinoKey] && formasPago.find(fp => fp.fop_id === formaPagoUnificada[destinoKey])?.fop_nombre?.toUpperCase().trim() === 'EFECTIVO' && (descuentoUnificado[destinoKey] || 0) > 0 && (() => {
                            const saldoMaxPago = destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
                            const montoBasePago = montoPagoUnificado[destinoKey] ?? saldoMaxPago;
                            const pctDesc = descuentoUnificado[destinoKey] || 0;
                            const montoCobrar = montoACobrarConDescuentoEfectivo(montoBasePago, pctDesc);
                            const montoDescuento = Math.round(montoBasePago * (pctDesc / 100) * 100) / 100;
                            return (
                              <div
                                style={{
                                  marginBottom: 16,
                                  padding: '12px 14px',
                                  background: '#252525',
                                  borderRadius: 8,
                                  border: '1px solid #3a3a3a',
                                }}
                              >
                                <Text style={{ color: '#a0a0a0', fontSize: 12, display: 'block', marginBottom: 6 }}>
                                  Total a cobrar (con descuento aplicado)
                                </Text>
                                <Text strong style={{ color: '#52c41a', fontSize: 22 }}>
                                  ${montoCobrar.toFixed(2)}
                                </Text>
                                <Text style={{ color: '#888', fontSize: 12, display: 'block', marginTop: 8, lineHeight: 1.5 }}>
                                  Monto base: ${montoBasePago.toFixed(2)} · Descuento {pctDesc}% (−${montoDescuento.toFixed(2)})
                                </Text>
                              </div>
                            );
                          })()}
                          {formaPagoUnificada[destinoKey] && formasPago.find(fp => fp.fop_id === formaPagoUnificada[destinoKey])?.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE' && (
                            <Form.Item label={<Text style={{ color: '#ffffff' }}>Jugador</Text>} required>
                              <Select
                                placeholder="Seleccione jugador"
                                value={jugadorUnificado[destinoKey]}
                                onChange={(v) => setJugadorUnificado(prev => ({ ...prev, [destinoKey]: v }))}
                                showSearch
                                filterOption={(i, o) => String(o?.children ?? '').toLowerCase().includes((i ?? '').toLowerCase())}
                                style={{ width: '100%' }}
                              >
                                {jugadores.map(j => (
                                  <Select.Option key={j.jcl_id} value={j.jcl_id}>{j.jcl_nombre} {j.jcl_apellido}</Select.Option>
                                ))}
                              </Select>
                            </Form.Item>
                          )}
                          <Button
                            type="primary"
                            icon={<CheckOutlined />}
                            loading={loadingPagoUnificado[destinoKey]}
                            block
                            onClick={() => (ventas.length > 0 ? handleRegistrarPagoUnificado(destinoKey, ventas, destino) : handleCobrarTurno(destino))}
                            disabled={
                              (() => {
                                const saldoMax = destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0);
                                const montoEfectivo = montoPagoUnificado[destinoKey] ?? saldoMax;
                                return !montoEfectivo || montoEfectivo <= 0 || !formaPagoUnificada[destinoKey] ||
                                  (formasPago.find(fp => fp.fop_id === formaPagoUnificada[destinoKey])?.fop_nombre?.toUpperCase().trim() === 'CUENTA CORRIENTE' && !jugadorUnificado[destinoKey]);
                              })()
                            }
                          >
                            {montoPagoUnificado[destinoKey] && montoPagoUnificado[destinoKey] < (destino.tipo === 'cancha_turno' ? destino.total_pendiente : ventas.reduce((s, v) => s + parseFloat(v.saldo_pendiente || 0), 0)) ? 'Pagar' : 'Pagar todo'}
                          </Button>
                        </Form>
                      </div>
                    ) : null}
                  </Space>
                </Card>
                </div>
                );
              })()}
        </Drawer>

        <Modal
              title="Agregar venta"
              open={modalAgregarVentaVisible}
              onCancel={() => {
                setModalAgregarVentaVisible(false);
                setDestinoParaVenta(null);
                setDetallesTemporal([]);
                setArticuloSeleccionado(null);
                setCantidadTemporal(1);
                setMesasVinculadasSeleccionadas([]);
              }}
              onOk={handleGuardarVentaCobros}
              okText="Guardar venta"
              confirmLoading={loadingAgregarVenta}
              cancelText="Cancelar"
              width={isMobile ? '100%' : 640}
              style={isMobile ? { maxWidth: '100%', top: 8, paddingBottom: 0 } : undefined}
              destroyOnClose
              styles={{ body: { backgroundColor: '#1a1a1a', paddingBottom: 8 }, header: { backgroundColor: '#2d2d2d', borderBottom: '1px solid #434343' } }}
            >
              {destinoParaVenta && (
                <div style={{ marginBottom: 16 }}>
                  <Text style={{ color: '#b0b0b0' }}>Destino: </Text>
                  <Text strong style={{ color: '#ffffff' }}>{destinoParaVenta.destino?.nombre}</Text>
                </div>
              )}
              {destinoParaVenta?.destino?.tipo === 'mesa' && mesasParaCobros.length > 1 && (
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: 14 }}>
                    Mesas adicionales ocupadas (opcional)
                  </label>
                  <Select
                    mode="multiple"
                    placeholder="Otras mesas que ocupa esta persona"
                    value={mesasVinculadasSeleccionadas}
                    onChange={setMesasVinculadasSeleccionadas}
                    style={{ width: '100%' }}
                    optionFilterProp="label"
                    listHeight={100}
                    getPopupContainer={() => document.body}
                    options={mesasParaCobros
                      .filter(m => m.id !== destinoParaVenta.destino?.id && (m.cantidad_ventas ?? 0) === 0)
                      .map(m => ({ value: m.id, label: m.nombre }))}
                  />
                  <Text type="secondary" style={{ fontSize: 12, color: '#8c8c8c', display: 'block', marginTop: 4 }}>
                    Si la persona ocupa más de una mesa, seleccioná las demás aquí. Las gastos se cargan en la mesa principal y las otras quedarán marcadas como ocupadas. Hacé clic fuera o en el siguiente campo para cerrar la lista.
                  </Text>
                </div>
              )}
              <Divider style={{ borderColor: '#434343' }}>Detalles de la Venta</Divider>
              <div style={{
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                flexWrap: isMobile ? 'wrap' : 'nowrap',
                gap: 8,
                marginBottom: 16,
                alignItems: isMobile ? 'stretch' : 'flex-end',
              }}>
                <div style={{ flex: isMobile ? 'none' : 3.2, minWidth: isMobile ? undefined : 175 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: 14 }}>Artículo</label>
                  <Select
                    ref={articuloSelectRef}
                    placeholder="Seleccione un artículo"
                    value={articuloSeleccionado}
                    onChange={setArticuloSeleccionado}
                    showSearch
                    style={{ width: '100%' }}
                    getPopupContainer={(node) => node.parentElement || document.body}
                    filterOption={(input, option) =>
                      String(option?.children || '').toLowerCase().includes((input || '').toLowerCase())
                    }
                  >
                    {articulos
                      .filter(art => !detallesTemporal.some(d => d.articulo_id === art.art_id))
                      .map(art => (
                        <Select.Option key={art.art_id} value={art.art_id}>
                          {art.art_nombre} - Stock: {art.art_stock_actual}
                        </Select.Option>
                      ))}
                  </Select>
                </div>
                <div style={{ flex: isMobile ? 'none' : 0.5, minWidth: isMobile ? undefined : 70 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: 14 }}>Cantidad</label>
                  <InputNumber
                    min={1}
                    value={cantidadTemporal}
                    onChange={(v) => setCantidadTemporal(v || 1)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAgregarDetalleCobros())}
                    style={{ width: '100%' }}
                    placeholder="Cant."
                  />
                </div>
                <div style={{ flex: isMobile ? 'none' : 1.3, minWidth: isMobile ? undefined : 125 }}>
                  <label style={{ display: 'block', marginBottom: 4, color: '#ffffff', fontSize: 14 }}>Precio</label>
                  <div style={{
                    padding: '4px 11px',
                    minHeight: 32,
                    backgroundColor: '#1a1a1a',
                    borderRadius: 6,
                    border: '1px solid #434343',
                    color: '#d9d9d9',
                    display: 'flex',
                    alignItems: 'center',
                  }}>
                    $ {articuloSeleccionado
                      ? (articulos.find(a => a.art_id === articuloSeleccionado)?.art_precio_venta ?? 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : '0,00'}
                  </div>
                </div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAgregarDetalleCobros}
                  style={{
                    height: isMobile ? 44 : 32,
                    minHeight: isMobile ? 44 : 32,
                    width: isMobile ? '100%' : undefined,
                    touchAction: 'manipulation',
                  }}
                >
                  Agregar
                </Button>
              </div>
              {detallesTemporal.length > 0 && (
                <Table
                  dataSource={detallesTemporal}
                  rowKey={(r, i) => `${r.articulo_id}-${i}`}
                  pagination={false}
                  size="small"
                  columns={[
                    { title: 'Artículo', dataIndex: 'articulo_nombre', key: 'articulo_nombre' },
                    { title: 'Cantidad', dataIndex: 'cantidad', key: 'cantidad', width: 90, align: 'center' },
                    { title: 'Precio unit.', dataIndex: 'precio_unitario', key: 'precio_unitario', width: 110, align: 'right', render: (p) => `$${parseFloat(p).toFixed(2)}` },
                    { title: 'Subtotal', dataIndex: 'subtotal', key: 'subtotal', width: 110, align: 'right', render: (s) => `$${parseFloat(s).toFixed(2)}` },
                    {
                      title: 'Acción',
                      key: 'accion',
                      width: 80,
                      align: 'center',
                      render: (_, __, index) => (
                        <Button type="link" danger size="small" icon={<DeleteOutlined />} onClick={() => handleQuitarDetalleCobros(index)}>
                          Quitar
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </Modal>
      </div>
    </MainLayout>
  );
};

export default Cobros;
